import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaPhone, FaMapMarkerAlt, FaEnvelope, FaWhatsapp, FaInstagram } from 'react-icons/fa';
import Clock from 'react-live-clock';
import logo from '../assets/Navbar/logo.jpg';

const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '';
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
};

const Navbar = () => {
    const languages = [
        { code: 'en', name: 'English', country: 'GB' },   // United Kingdom flag for English
        { code: 'ja', name: 'Japanese', country: 'JP' },
        { code: 'de', name: 'German', country: 'DE' },
        { code: 'ru', name: 'Russian', country: 'RU' },
        { code: 'pl', name: 'Polish', country: 'PL' },
        { code: 'it', name: 'Italian', country: 'IT' },

        { code: 'zh', name: 'Chinese', country: 'CN' },   // Simplified Chinese → China flag
    ];

    const { t, i18n } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    // === NAVIGATION ITEMS (tradotti) ===
    const navItems = [
        { to: '/', label: t('home') },
        { to: '/about-us', label: t('aboutUs') },
        { to: '/japan', label: t('sriLankanav') },
        { to: '/tour', label: t('bookATour') },
        { to: '/gallery', label: t('gallerynav') },
        { to: '/things', label: t('thingsToDo') },
        { to: '/contact-us', label: t('contactUs') },
    ];

    return (
        <div className="relative">
            {/* Top Section */}
            <div
                className={`flex items-center justify-between text-white ${isScrolled ? 'shadow-[0_10px_30px_rgba(0,0,0,0.25)]' : ''
                    } bg-white border-b border-[#07111f]/10 px-4 sm:px-6 lg:px-8 relative h-40 sm:h-48 md:h-48 transition-all duration-300`}
            >
                {/* Centered Logo */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <img
                        src={logo}
                        alt="Ceylon Tours"
                        className="h-48 sm:h-48 md:h-48 lg:h-48 w-auto object-contain"
                    />
                </div>

                {/* Clock Component (Desktop lg+) */}
                <div className="hidden lg:flex absolute top-4 right-4 flex-col items-end gap-1.5">
                    <div className="rounded-lg border border-white/10 bg-[#0f1d33] px-3 py-1.5 text-sm font-medium text-white shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
                        <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Tokyo'} />
                    </div>
                    <h3 className="mr-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#f7d774] sm:text-sm">Japan</h3>
                </div>

                {/* Clock Component (Tablet md only) */}
                <div className="hidden md:flex lg:hidden absolute top-4 right-4 flex-col items-end gap-1">
                    <div className="rounded-lg border border-white/10 bg-[#0f1d33] px-2.5 py-1 text-xs font-medium text-white shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
                        <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Tokyo'} />
                    </div>
                    <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#f7d774]">Japan</h3>
                </div>

                {/* Language Selector and Social Icons (Desktop lg+) */}
                <div className="hidden lg:flex flex-col gap-3 absolute top-20 right-2 sm:right-3">
                    {/* Language Dropdown - Solo IT/EN */}
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="lg:ml-32 rounded-lg border border-white/10 bg-[#0f1d33] px-3 py-1.5 text-sm text-white shadow-[0_20px_50px_rgba(0,0,0,0.25)] sm:px-4 sm:py-2 sm:text-base w-20 sm:w-36 transition-all hover:bg-[#7fd3ff] hover:text-[#07111f] focus:outline-none focus:ring-2 focus:ring-[#7fd3ff]"
                        aria-label="Select language"
                    >
                        {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                                {getFlagEmoji(lang.country)} {lang.name}
                            </option>
                        ))}
                    </select>

                    {/* Social Icons */}
                    <div className="flex gap-3 sm:gap-4 sm:mt-8 md:mt-4 ml-20">
                        <a href="tel:+819082514053" aria-label="Chiamaci">
                            <FaPhone className="w-5 h-5 sm:w-6 sm:h-6 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/HPt5ohC5Us9WHb9u7"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Vedi posizione"
                        >
                            <FaMapMarkerAlt className="w-5 h-5 sm:w-6 sm:h-6 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                        <a href="mailto:vbuddika@sonictourssl.com" aria-label="Scrivici">
                            <FaEnvelope className="w-5 h-5 sm:w-6 sm:h-6 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://wa.me/+819082514053"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contattaci su WhatsApp"
                        >
                            <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.instagram.com/sonictours.japan?igsh=MTgzYWtka2lnNzV5ag=="
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visita il nostro Instagram"
                        >
                            <FaInstagram className="w-5 h-5 sm:w-6 sm:h-6 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                    </div>
                </div>

                {/* Language Selector and Social Icons (Tablet md only) */}
                <div className="hidden md:flex lg:hidden flex-col gap-2 absolute top-16 right-2">
                    {/* Language Dropdown */}
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="rounded-lg border border-white/10 bg-[#0f1d33] px-2.5 py-1 text-xs text-white shadow-[0_20px_50px_rgba(0,0,0,0.25)] w-28 transition-all hover:bg-[#7fd3ff] hover:text-[#07111f] focus:outline-none focus:ring-2 focus:ring-[#7fd3ff]"
                        aria-label="Select language"
                    >
                        {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                                {getFlagEmoji(lang.country)} {lang.name}
                            </option>
                        ))}
                    </select>

                    {/* Social Icons */}
                    <div className="flex gap-2.5 mt-2 ml-8">
                        <a href="tel:+819082514053" aria-label="Chiamaci">
                            <FaPhone className="w-4 h-4 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/HPt5ohC5Us9WHb9u7"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Vedi posizione"
                        >
                            <FaMapMarkerAlt className="w-4 h-4 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                        <a href="mailto:vbuddika@sonictourssl.com" aria-label="Scrivici">
                            <FaEnvelope className="w-4 h-4 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://wa.me/+819082514053"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contattaci su WhatsApp"
                        >
                            <FaWhatsapp className="w-4 h-4 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.instagram.com/sonictours.japan?igsh=MTgzYWtka2lnNzV5ag=="
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visita il nostro Instagram"
                        >
                            <FaInstagram className="w-4 h-4 text-[#0f1d33] hover:text-[#7fd3ff] transition-transform transform hover:scale-110" />
                        </a>
                    </div>
                </div>

                {/* Clock Component (Mobile) */}
                <div className="md:hidden absolute top-4 left-4 flex flex-col items-start gap-1.5">
                    <div className="rounded-lg border border-white/10 bg-[#0f1d33] px-2 py-1 text-xs font-medium text-white shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
                        <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Tokyo'} />
                    </div>
                    <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#f7d774]">Japan</h3>
                </div>

                {/* Language Selector and Menu Button (Mobile) */}
                <div className="md:hidden flex flex-col gap-3 absolute top-4 right-4">
                    {/* Language Dropdown - Solo IT/EN */}


                    {/* Menu Button */}
                    <button
                        className="text-[#0074D9] bg-transparent border-none mt-10 ml-20"
                        onClick={toggleMenu}
                        aria-label="Apri menu mobile"
                    >
                        <svg
                            className="w-7 h-7 sm:w-8 sm:h-8"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 6h16M4 12h16m-7 6h7"
                            ></path>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Bottom Section (Desktop Navigation) */}
            <div
                className="hidden md:flex justify-center items-center px-4 sm:px-6 lg:px-8 py-2 bg-[#0f1d33] sticky top-0 z-50 border-t border-white/10"
                style={{ boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none' }}
            >
                <ul className="flex flex-wrap justify-center gap-2 sm:gap-4 lg:gap-6 mb-0 font-serif text-lg sm:text-xl lg:text-2xl">
                    {navItems.map((item) => (
                        <li key={item.to} className="nav-item">
                            <NavLink
                                to={item.to}
                                className={({ isActive }) =>
                                    `nav-link px-3 py-2 sm:px-4 sm:py-2 font-copperplate text-white hover:text-white transition-colors rounded-md ${isActive ? 'underline bg-[#7FDBFF] text-white font-bold font-copperplate' : ''
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div
                    className="md:hidden fixed top-0 right-0 h-full w-3/4 sm:w-2/3 max-w-xs bg-[#0f1d33] shadow-2xl border-l border-white/10 z-50 p-4 sm:p-5 rounded-l-lg animate-slideIn"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                >
                    <style>
                        {`
              @keyframes slideIn {
                from {
                  transform: translateX(100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
                    </style>

                    {/* Close Button */}
                    <button
                        className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#f7d774]/40 bg-[#07111f] text-xl font-bold text-[#f7d774] transition-all hover:scale-110 hover:bg-[#7fd3ff] hover:text-[#07111f] sm:h-12 sm:w-12 sm:text-2xl"
                        onClick={toggleMenu}
                        aria-label="Chiudi menu mobile"
                    >
                        X
                    </button>
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="mt-20 w-32 rounded-lg border border-white/10 bg-[#0f1d33] py-1.5 text-sm text-white shadow-[0_20px_50px_rgba(0,0,0,0.25)] transition-all hover:bg-[#7fd3ff] hover:text-[#07111f] focus:outline-none focus:ring-2 focus:ring-[#7fd3ff]"
                        aria-label="Select language"
                    >
                        {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                                {getFlagEmoji(lang.country)} {lang.name}
                            </option>
                        ))}
                    </select>

                    {/* Navigation Links */}
                    <ul className="flex flex-col mt-16 gap-2">
                        {navItems.map((item) => (
                            <li key={item.to} className="nav-item">
                                <NavLink
                                    to={item.to}
                                    onClick={toggleMenu}
                                    className={({ isActive }) =>
                                        `font-copperplate nav-link text-white hover:bg-[#7fd3ff] hover:text-[#07111f] transition-all  text-lg sm:text-xl py-2 px-4 block rounded-md ${isActive ? 'underline bg-[#7fd3ff] text-[#07111f] font-bold' : ''
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {/* Social Icons (Mobile) */}
                    <div className="flex gap-3 sm:gap-4 mt-4 justify-center border-t border-gray-200 pt-4">
                        <a href="tel:+819082514053" aria-label="Chiamaci" className="text-[#001F3F] hover:text-[#7FDBFF] transition-transform transform hover:scale-110">
                            <FaPhone className="w-5 h-5 sm:w-6 sm:h-6 text-[#7fd3ff] hover:text-[#f7d774]" />
                        </a>
                        <a href="https://maps.app.goo.gl/HPt5ohC5Us9WHb9u7" target="_blank" rel="noopener noreferrer" aria-label="Vedi posizione" className="text-[#001F3F] hover:text-[#7FDBFF] transition-transform transform hover:scale-110">
                            <FaMapMarkerAlt className="w-5 h-5 sm:w-6 sm:h-6 text-[#7fd3ff] hover:text-[#f7d774]" />
                        </a>
                        <a href="mailto:vbuddika@sonictourssl.com" aria-label="Scrivici" className="text-[#001F3F] hover:text-[#7FDBFF] transition-transform transform hover:scale-110">
                            <FaEnvelope className="w-5 h-5 sm:w-6 sm:h-6 text-[#7fd3ff] hover:text-[#f7d774]" />
                        </a>
                        <a href="https://wa.me/+819082514053" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-[#001F3F] hover:text-[#7FDBFF] transition-transform transform hover:scale-110">
                            <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6 text-[#7fd3ff] hover:text-[#f7d774]" />
                        </a>
                        <a href="https://www.instagram.com/sonictours.japan?igsh=MTgzYWtka2lnNzV5ag==" target="_blank" rel="noopener noreferrer" aria-label="Visita Instagram" className="text-[#001F3F] hover:text-[#7FDBFF] transition-transform transform hover:scale-110">
                            <FaInstagram className="w-5 h-5 sm:w-6 sm:h-6 text-[#7fd3ff] hover:text-[#f7d774]" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;